import React, { useEffect } from "react";
import { gsap } from "gsap";
import AOS from "aos";

// Components
import InfiniteScroll from "../home/InfiniteScroll";
import ThreeScene from "../home/ThreeScene";
import Clarifications from "./Clarifications";
import EventCarousel from "./EventCarousel";
import RulesAndFAQs from "./RulesAndFAQs";
import LandingPage from "./LandingPage";

// Utils
import { WINTER_OF_CODE_7 } from "../../utils/data/winter-of-code";

// Images
import WINTER_OF_CODE_POSTER from "../../images/events/winter-of-code/winter-of-code-7/Poster.png";
import TIMELINE_IMG from "../../images/events/winter-of-code/winter-of-code-7/Timeline.png";

// Material UI Components
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";

export default function WinterOfCodeHomePage() {
  const WoC = "Winter of Code 7️⃣";
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    // Function to handle mouse movement
    const handleMouseMove = (event) => {
      const elements = document.querySelectorAll(".moving-poster");
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const offsetX = (event.clientX - (rect.left + rect.width / 2)) / 400; // Minor horizontal movement
        const offsetY = (event.clientY - (rect.top + rect.height / 2)) / 400; // Minor vertical movement

        // Apply GSAP animation for smooth movement
        gsap.to(element, {
          x: offsetX,
          y: offsetY,
          duration: 0.3,
          ease: "power2.out",
        });
      });
    };

    // Attach the mousemove event listener
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      // Cleanup the event listener on unmount
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    // Hover effect for rotation
    const elements = document.querySelectorAll(".moving-poster");
    elements.forEach((element) => {
      element.addEventListener("mouseenter", () => {
        gsap.to(element, {
          rotate: 2, // Rotate 15 degrees
          duration: 0.5,
          ease: "power2.out",
        });
      });
      element.addEventListener("mouseleave", () => {
        gsap.to(element, {
          rotate: 0, // Reset rotation
          duration: 0.5,
          ease: "power2.out",
        });
      });
    });

    return () => {
      elements.forEach((element) => {
        element.removeEventListener("mouseenter", null);
        element.removeEventListener("mouseleave", null);
      });
    };
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={12} md={12} lg={7} xl={8}>
          <LandingPage />
        </Grid>
        {isSmallScreen ? (
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={5}
            xl={4}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              top: { xs: "50%", sm: "50%", md: "0%", lg: "0%", xl: "0%" },
              left: { xs: "50%", sm: "50%", md: "0%", lg: "0%", xl: "0%" },
              transform: {
                xs: "translate(-50%, -50%)",
                sm: "translate(-50%, -50%)",
                md: "translate(0%, 0%)",
                lg: "translate(0%, 0%)",
                xl: "translate(0%, 0%)",
              },
              zIndex: { xs: -1, sm: -1, md: 0, lg: 0, xl: 0 },
              position: {
                xs: "absolute",
                sm: "absolute",
                md: "relative",
                lg: "relative",
                xl: "relative",
              },
            }}
          >
            <ThreeScene />
          </Grid>
        ) : null}
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={5}
          xl={4}
          sx={{
            display: "flex",
            justifyContent: {
              xs: "center",
              sm: "center",
              md: "center",
              lg: "flex-start",
              xl: "flex-start",
            },
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              img: {
                xs: { width: 300 },
                sm: { width: 400 },
                md: { width: 400 },
                lg: { width: 400 },
                xl: { width: 400 },
              },
            }}
          >
            <img
              className="moving-poster"
              src={WINTER_OF_CODE_POSTER}
              alt="WINTER_OF_CODE_POSTER"
              style={{
                borderRadius: 16,
                transition: "all 0.04s ease-in-out",
              }}
            />
          </Box>
        </Grid>
      </Grid>
      <InfiniteScroll data={WoC} />
      <Box sx={{ mt: 22 }}>&nbsp;</Box>
      <Box
        data-aos="fade-up"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          transition: "all 0.3s ease-in-out",
          img: {
            "&:hover": {
              transform: "rotate(1deg)",
              transition: "all 0.3s ease-in-out",
            },
            transition: "all 0.3s ease-in-out",
            width: {
              xs: "80vw", // For extra small screens (mobile)
              sm: "50vw", // For small screens
              md: "50vw", // For medium screens
              lg: "36vw", // For large screens and above
              xl: "30vw", // For large screens and above
            },
            borderRadius: {
              xs: 4, // Smaller radius for mobile screens
              sm: 5, // Slightly larger for small screens
              md: 5, // Standard radius for medium screens
              lg: 6, // Larger radius for large screens
              xl: 6, // Larger radius for large screens
            },
          },
        }}
      >
        <img src={TIMELINE_IMG} alt="TIMELINE_IMG" />
      </Box>

      <Box sx={{ mt: 12 }}>&nbsp;</Box>
      <EventCarousel slides={WINTER_OF_CODE_7} />
      <Box sx={{ mb: 10 }}>&nbsp;</Box>
      <RulesAndFAQs />
      <Box sx={{ mb: 6 }}>&nbsp;</Box>
      <Clarifications />
      <Box sx={{ mb: 20 }}>&nbsp;</Box>
    </>
  );
}
